import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const STORAGE_KEY = 'wanna_install_prompt';
const DISMISS_DAYS = 7;

export default function InstallPrompt({ language = 'es' }) {
  const { theme } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const ageDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (ageDays < DISMISS_DAYS) return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.removeItem(STORAGE_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const onDismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-0 right-0 z-50 px-3 install-prompt-wrapper"
      style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }}
      data-testid="install-prompt"
    >
      <div className={`mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-border bg-surface p-3 ${theme === 'dark' ? 'shadow-none' : 'shadow-lg'}`}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-foreground text-background">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-foreground">
            {language === 'es' ? 'Instalá WannaColive' : 'Install WannaColive'}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {language === 'es' ? 'Accedé más rápido desde tu pantalla de inicio.' : 'Open faster from your home screen.'}
          </p>
        </div>
        <button
          onClick={onInstall}
          className="flex-shrink-0 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background transition-opacity hover:opacity-90"
          data-testid="install-prompt-cta"
        >
          {language === 'es' ? 'Instalar' : 'Install'}
        </button>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Dismiss"
          data-testid="install-prompt-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
